import type { StepFlowLang } from '@/components/step-flow/StepFlowShell.types';
import type { PiiTrainingCopy } from '@/pages/pii-training/piiTraining.types';

export const PII_TRAINING_TOTAL_STEPS = 4;

const is: PiiTrainingCopy = {
  stepLabel: (step, totalSteps) => `Skref ${step} af ${totalSteps}`,
  nav: {
    back: 'Til baka',
    next: 'Áfram',
    complete: 'Ljúka fræðslu',
    submitting: 'Skrái...',
  },
  whatIsPii: {
    title: 'Hvað eru persónuupplýsingar?',
    subtitle: 'Sýnikennsla: Diktering og persónuvernd',
    paragraphs: [
      'Áður en þú hefst handa við að diktera inn nótur er nauðsynlegt að þú kynnir þér eftirfarandi atriði er varða persónuvernd og persónugreinanlegar upplýsingar.',
      'Við dikteringu er alltaf ákveðin hætta á að persónugreinanlegar upplýsingar um skjólstæðing sjálfan eða þriðja aðila komi fram.',
      'Til þess að lágmarka þessa áhættu er mikilvægt að heilbrigðisstarfsmaður sé meðvitaður um hana og gæti þess eftir fremsta megni að útfæra diktöt þannig að ekki komi fram persónugreinanlegar upplýsingar um skjólstæðing eða þriðja aðila.',
      'Persónugreinanlegar upplýsingar eru allar upplýsingar um tiltekinn einstakling sem gera þriðja aðila kleift að auðkenna þann einstakling.',
    ],
    obviousHeading: 'Dæmi um augljósar persónugreinanlegar upplýsingar:',
    obviousExamples: [
      'Fullt nafn',
      'Kennitala',
      'Starf eða staða, t.d. ef um þjóðþekktan einstakling er að ræða eða einstakling innan lítils samfélags sem er vel þekktur í sínu nærsamfélagi',
    ],
    illustration: {
      heading: 'Úr diktati',
      snippet: [
        { text: 'Jón Jónsson', pii: true },
        { text: ', ' },
        { text: 'kt. 010190-0000', pii: true },
        { text: ', ' },
        { text: 'fyrirliði landsliðsins', pii: true },
        { text: ', kemur vegna verkja í öxl.' },
      ],
      footer: '3 persónugreinanleg atriði',
    },
  },
  combinations: {
    title: 'Samansafn upplýsinga',
    subtitle: 'Upplýsingar sem virðast saklausar einar og sér geta auðkennt einstakling þegar þær koma saman.',
    combinationsHeading: 'Dæmi um samansafn slíkra upplýsinga eru t.d.:',
    combinationExamples: [
      'Aldur, kyn og heimilisfang',
      'Aldur, kyn, starf og vinnuveitandi (sérstaklega á smærri vinnustöðum)',
      'Aldur, kyn, sveitarfélag og fjöldi barna (t.d. ef um er að ræða einstakling í fámennu sveitarfélagi á landsbyggðinni)',
    ],
    goodRuleTitle: 'Góð regla:',
    goodRuleBody:
      'Ef þú ert í vafa er góð regla að gera ráð fyrir því að um persónugreinanlegar upplýsingar sé að ræða og eyða upptökunni.',
    illustration: {
      heading: 'Úr diktati',
      snippet: [
        { text: '34 ára', pii: true },
        { text: ' ' },
        { text: 'kona', pii: true },
        { text: ' frá ' },
        { text: 'Tjörneshreppi', pii: true },
        { text: ' sem ' },
        { text: 'eignaðist tvíbura', pii: true },
        { text: ' fyrir tveimur mánuðum.' },
      ],
      footer: 'Ekkert eitt atriði dugar, en saman þekkja þau hana.',
    },
  },
  quiz: {
    title: 'Æfing: Er þetta persónugreinanlegt?',
    subtitle: 'Svaraðu já eða nei við hverju dæmi.',
    exampleLabel: (index) => `Dæmi ${index}`,
    yes: 'já',
    no: 'nei',
    questions: [
      {
        id: 'example1',
        scenario:
          '28 ára karlmaður leitar til læknis vegna verkja í öxl. Hefur ekki getað unnið eða sinnt heimilisstörfum síðustu viku. Er atvinnuhandboltamaður og fyrirliði landsliðsins, spilar í Þýskalandi, með maka og 2 börn. Versnaði eftir áverka í landsleik fyrir viku síðan.',
        correctAnswer: 'yes',
        feedbackCorrect:
          'Rétt! Þessar upplýsingar eru persónugreinanlegar þar sem fyrirliði landsliðsins er auðþekktur.',
        feedbackIncorrect:
          'Rangt. Þessar upplýsingar eru persónugreinanlegar þar sem fyrirliði landsliðsins er auðþekktur.',
        keyDetail: 'Fyrirliði landsliðsins',
      },
      {
        id: 'example2',
        scenario:
          '34 ára kona frá Tjörneshreppi leitar á heilsugæslu vegna höfuðverkja. Verið streita og álag á henni undanfarið. Mörg járn í eldinum. Eignaðist tvíbura fyrir 2 mánuðum síðan.',
        correctAnswer: 'yes',
        feedbackCorrect:
          'Rétt! Tjörneshreppur er fámenn sveitarfélag og tvíburafæðingar eru sjaldgæfar, sem gerir þetta persónugreinanlegt.',
        feedbackIncorrect:
          'Rangt. Tjörneshreppur er fámenn sveitarfélag og tvíburafæðingar eru sjaldgæfar, sem gerir þetta persónugreinanlegt.',
        keyDetail: 'Fámennt sveitarfélag og tvíburar',
      },
      {
        id: 'example3',
        scenario:
          '70 ára karl úr Vesturbænum með sögu um góðkynja stækkun á blöðruhálskirtli leitar á heilsugæslu vegna vaxandi þvaglátaeinkenna síðustu þrjár vikur. Kemur í viðtal ásamt maka. Ern miðað við aldur. Hættur að vinna en starfaði lengst af við afgreiðslustörf.',
        correctAnswer: 'no',
        feedbackCorrect:
          'Rétt! Þessar upplýsingar eru ekki nægilega sértækar til að auðkenna einstakling í fjölmennum hverfum eins og Vesturbæ.',
        feedbackIncorrect:
          'Rangt. Þessar upplýsingar eru ekki nægilega sértækar til að auðkenna einstakling í fjölmennum hverfum eins og Vesturbæ.',
        keyDetail: 'Fjölmennt hverfi, algengt starf',
      },
    ],
    unansweredHint: 'Vinsamlegast svaraðu öllum spurningum og merktu við samþykki til að ljúka fræðslunni.',
    illustration: {
      heading: 'Þín svör',
      unanswered: 'Ósvarað',
      correct: 'Rétt',
      incorrect: 'Rangt',
      identifiable: 'Persónugreinanlegt',
      notIdentifiable: 'Ekki persónugreinanlegt',
    },
  },
  consent: {
    title: 'Samþykki',
    subtitle: 'Stutt samantekt áður en þú lýkur fræðslunni.',
    consentLabel: 'Ég samþykki hér með að hafa kynnt mér fræðsluefni Careflux um persónugreinanlegar upplýsingar.',
    illustration: {
      heading: 'Yfirferð',
      items: {
        obvious: 'Augljós auðkenni',
        combinations: 'Samansafn upplýsinga',
        quiz: 'Æfing',
        consent: 'Samþykki',
      },
    },
  },
  toast: {
    successTitle: 'Fræðslu lokið',
    successDescription: 'Þakka þér fyrir að ljúka persónuverndafræðslunni.',
    errorTitle: 'Villa',
    errorDescription: 'Ekki tókst að skrá fræðsluna. Vinsamlegast reyndu aftur.',
  },
};

const en: PiiTrainingCopy = {
  stepLabel: (step, totalSteps) => `Step ${step} of ${totalSteps}`,
  nav: {
    back: 'Back',
    next: 'Continue',
    complete: 'Complete training',
    submitting: 'Submitting...',
  },
  whatIsPii: {
    title: 'What is personal information?',
    subtitle: 'Walkthrough: Dictation and privacy',
    paragraphs: [
      'Before you start dictating notes, you need to familiarise yourself with the following points on privacy and personally identifiable information.',
      'When dictating, there is always some risk that personally identifiable information about the patient or a third party is disclosed.',
      'To minimise this risk, it is important that healthcare professionals are aware of it and do their utmost to phrase dictations so that no personally identifiable information about the patient or a third party is included.',
      'Personally identifiable information is any information about a specific individual that enables a third party to identify that individual.',
    ],
    obviousHeading: 'Examples of obviously personally identifiable information:',
    obviousExamples: [
      'Full name',
      'National ID number (kennitala)',
      'Occupation or position, e.g. for a nationally known person or someone well known within a small local community',
    ],
    illustration: {
      heading: 'From a dictation',
      snippet: [
        { text: 'Jón Jónsson', pii: true },
        { text: ', ' },
        { text: 'ID 010190-0000', pii: true },
        { text: ', ' },
        { text: 'national team captain', pii: true },
        { text: ', presents with shoulder pain.' },
      ],
      footer: '3 identifying details',
    },
  },
  combinations: {
    title: 'Combined information',
    subtitle: 'Details that seem harmless on their own can identify a person when they are combined.',
    combinationsHeading: 'Examples of such combinations include:',
    combinationExamples: [
      'Age, sex and home address',
      'Age, sex, occupation and employer (especially at smaller workplaces)',
      'Age, sex, municipality and number of children (e.g. for a person in a sparsely populated rural municipality)',
    ],
    goodRuleTitle: 'A good rule:',
    goodRuleBody:
      'If you are in doubt, a good rule is to assume that the information is personally identifiable and delete the recording.',
    illustration: {
      heading: 'From a dictation',
      snippet: [
        { text: 'A ' },
        { text: '34-year-old', pii: true },
        { text: ' ' },
        { text: 'woman', pii: true },
        { text: ' from ' },
        { text: 'Tjörneshreppur', pii: true },
        { text: ' who ' },
        { text: 'had twins', pii: true },
        { text: ' two months ago.' },
      ],
      footer: 'No single detail is enough, but together they identify her.',
    },
  },
  quiz: {
    title: 'Exercise: Is this personally identifiable?',
    subtitle: 'Answer yes or no for each example.',
    exampleLabel: (index) => `Example ${index}`,
    yes: 'yes',
    no: 'no',
    questions: [
      {
        id: 'example1',
        scenario:
          'A 28-year-old man sees a doctor for shoulder pain. He has been unable to work or do household chores for the past week. He is a professional handball player and captain of the national team, plays in Germany, and has a partner and 2 children. It got worse after an injury in an international match a week ago.',
        correctAnswer: 'yes',
        feedbackCorrect:
          'Correct! This information is identifiable because the national team captain is easily recognised.',
        feedbackIncorrect:
          'Incorrect. This information is identifiable because the national team captain is easily recognised.',
        keyDetail: 'National team captain',
      },
      {
        id: 'example2',
        scenario:
          'A 34-year-old woman from Tjörneshreppur visits a health clinic for headaches. She has been under stress and pressure lately, with many irons in the fire. She gave birth to twins 2 months ago.',
        correctAnswer: 'yes',
        feedbackCorrect:
          'Correct! Tjörneshreppur is a small municipality and twin births are rare, which makes this identifiable.',
        feedbackIncorrect:
          'Incorrect. Tjörneshreppur is a small municipality and twin births are rare, which makes this identifiable.',
        keyDetail: 'Small municipality and twins',
      },
      {
        id: 'example3',
        scenario:
          'A 70-year-old man from Vesturbær with a history of benign prostatic enlargement visits a health clinic for worsening urinary symptoms over the past three weeks. He attends with his partner. Sprightly for his age. Retired, but worked mostly in retail.',
        correctAnswer: 'no',
        feedbackCorrect:
          'Correct! This information is not specific enough to identify an individual in a populous neighbourhood such as Vesturbær.',
        feedbackIncorrect:
          'Incorrect. This information is not specific enough to identify an individual in a populous neighbourhood such as Vesturbær.',
        keyDetail: 'Populous neighbourhood, common job',
      },
    ],
    unansweredHint: 'Please answer all questions and tick the consent box to complete the training.',
    illustration: {
      heading: 'Your answers',
      unanswered: 'Not answered',
      correct: 'Correct',
      incorrect: 'Incorrect',
      identifiable: 'Identifiable',
      notIdentifiable: 'Not identifiable',
    },
  },
  consent: {
    title: 'Consent',
    subtitle: 'A short recap before you complete the training.',
    consentLabel:
      'I hereby confirm that I have read the Careflux training material on personally identifiable information.',
    illustration: {
      heading: 'Recap',
      items: {
        obvious: 'Obvious identifiers',
        combinations: 'Combined information',
        quiz: 'Exercise',
        consent: 'Consent',
      },
    },
  },
  toast: {
    successTitle: 'Training complete',
    successDescription: 'Thank you for completing the privacy training.',
    errorTitle: 'Error',
    errorDescription: 'Could not record the training. Please try again.',
  },
};

export const piiTrainingContent: Record<StepFlowLang, PiiTrainingCopy> = { is, en };
